import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useActiveOnScreen } from "@/lib/useActiveOnScreen";

/* Hero portrait carousel — Richmond Vona, Kwartler Manus. */
const heroClients = [
    {
        name: "Lizzy DePalma",
        role: "Operations Lead · Richmond Vona PI",
        quote: "Faster connections, better conversations. Lexafide routes every call with full context, so our team just shows up and helps.",
        img: "/team/richmond-vona.jpg",
        imgAlt: "Richmond Vona PI law firm operations team",
        objectPosition: "center 35%",
        metricA: { icon: "fa-arrows-turn-right", value: "~50%", label: "Calls routed past front desk" },
        metricB: { icon: "fa-user-shield", value: "1 in 4", label: "Adjuster calls auto-routed" },
    },
    {
        name: "Kwartler Manus",
        role: "Personal Injury · Philadelphia",
        quote: "Our clients call in the worst moment of their lives. Lexafide knows who they are before we even pick up — so they're never a stranger to us.",
        img: "/team/kwartler-manus.jpg",
        imgAlt: "Attorney at Kwartler Manus Injury Lawyers, Philadelphia",
        objectPosition: "center 20%",
        metricA: { icon: "fa-id-card", value: "80%+", label: "Callers identified before pickup" },
        metricB: { icon: "fa-phone-volume", value: "1,300+", label: "Calls triaged · last 7 days" },
    },
];

const HeroSection = () => {
    const { ref: sectionRef } = useActiveOnScreen();

    return (
        <section
            ref={sectionRef}
            data-testid="hero-section"
            id="top"
            className="relative w-full overflow-hidden paper-grid warm-vignette pt-14 pb-20 md:pt-20 md:pb-28"
        >
            <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className="lg:col-span-7 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-6"
                    >
                        <span className="eyebrow-pill" data-testid="hero-live-stat">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)]" />
                            </span>
                            LexOPS · AI agents for legal operations
                        </span>
                    </motion.div>

                    <h1 className="font-display text-[clamp(2.2rem,4.8vw,3.8rem)] leading-[1.04] tracking-[-0.025em] text-[var(--ink)] font-semibold">
                        <motion.span
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.05 }}
                            className="inline"
                        >
                            Your name is on the door.{" "}
                        </motion.span>
                        <motion.span
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.15 }}
                            className="inline accent-blue"
                        >
                            Every call should sound like it.
                        </motion.span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="mt-7 max-w-xl text-lg md:text-[1.18rem] leading-relaxed text-[var(--ink-soft)]"
                    >
                        Lexafide handcrafts AI agents to your firm — answering every call, qualifying every lead, keeping every matter complete — the way you&apos;d do it yourself, if there were more of you.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55 }}
                        className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
                    >
                        <a
                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-book-demo-btn"
                            className="btn-primary inline-flex items-center gap-2 text-sm"
                        >
                            Book a 30-min demo
                            <i className="fa-solid fa-arrow-right text-[10px]" />
                        </a>
                        <a
                            href="#agents"
                            data-testid="hero-see-how-btn"
                            className="btn-ghost inline-flex items-center gap-2 text-sm"
                        >
                            See how it works
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.9 }}
                        data-testid="hero-trust-row"
                        className="mt-10 flex items-stretch gap-4"
                    >
                        <span className="w-[2px] bg-[var(--blue)] rounded-full flex-shrink-0" aria-hidden />
                        <div className="flex flex-col justify-center gap-1.5">
                            <div className="font-display text-[15px] leading-none text-[var(--ink)]">
                                <span className="font-semibold tabular-nums">50,000+</span> callers handled
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--ink-dim)]">
                                <span className="accent-blue">70+ hrs saved</span> per firm · this week
                            </div>
                        </div>
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative">
                    <HeroPortraitCard />
                </div>
            </div>
        </section>
    );
};

const HeroPortraitCard = () => {
    const [clientIdx, setClientIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const hasMultiple = heroClients.length > 1;
    const { ref: cardRef, active } = useActiveOnScreen();

    useEffect(() => {
        if (paused || !hasMultiple || !active) return;
        const t = setInterval(() => setClientIdx((i) => (i + 1) % heroClients.length), 6000);
        return () => clearInterval(t);
    }, [paused, hasMultiple, active]);

    const c = heroClients[clientIdx];

    return (
        <motion.div
            ref={cardRef}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-portrait-card"
            className="relative w-full max-w-[500px] mx-auto"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Card with crossfading photo + quote */}
            <div className="card-paper relative overflow-hidden aspect-[4/5]">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={`img-${clientIdx}`}
                        src={c.img}
                        alt={c.imgAlt || `${c.name}, ${c.role}`}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: c.objectPosition || "center" }}
                        loading="eager"
                    />
                </AnimatePresence>

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/70 to-transparent p-6 pt-20 text-[var(--paper)] z-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`txt-${clientIdx}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <p className="font-display font-medium text-[1.05rem] leading-[1.3] mb-3">
                                &ldquo;{c.quote}&rdquo;
                            </p>
                            <div>
                                <div className="text-sm font-semibold">{c.name}</div>
                                <div className="text-xs text-[var(--paper)]/70 font-mono tracking-wider uppercase">{c.role}</div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Progress dots — hidden when there's only one client */}
                {hasMultiple && (
                    <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
                        {heroClients.map((c, i) => (
                            <button
                                key={c.name || c.id || i}
                                type="button"
                                onClick={() => setClientIdx(i)}
                                data-testid={`hero-client-dot-${i}`}
                                aria-label={`Show client ${i + 1}`}
                                className="relative h-1.5 rounded-full overflow-hidden transition-all"
                                style={{ width: clientIdx === i ? "28px" : "10px", background: "rgba(255,255,255,0.35)" }}
                            >
                                {clientIdx === i && !paused && (
                                    <motion.span
                                        key={`p-${clientIdx}`}
                                        initial={{ width: 0 }}
                                        animate={{ width: "100%" }}
                                        transition={{ duration: 6, ease: "linear" }}
                                        className="absolute inset-y-0 left-0 bg-white"
                                    />
                                )}
                                {clientIdx === i && paused && (
                                    <span className="absolute inset-0 bg-white" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Floating metric A — top-left (hangs off the corner) */}
            <motion.div
                initial={{ opacity: 0, x: -16, y: -8 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.7, delay: 0.85 }}
                style={{ animation: "float-y 4.5s ease-in-out infinite" }}
                className="absolute -left-4 md:-left-12 top-[32%] card-paper pl-2.5 pr-3.5 py-2 z-20"
            >
                <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-[var(--blue-soft)] flex items-center justify-center accent-blue">
                        <i className={`fa-solid ${c.metricA.icon} text-[11px]`} />
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`mA-${clientIdx}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35 }}
                        >
                            <div className="font-display font-bold text-[17px] text-[var(--ink)] leading-none tabular-nums">{c.metricA.value}</div>
                            <div className="font-mono text-[9px] tracking-[0.14em] text-[var(--ink-dim)] uppercase mt-1 leading-tight">{c.metricA.label}</div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* Floating metric B — mid-right (hangs off the edge) */}
            <motion.div
                initial={{ opacity: 0, x: 16, y: 8 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.7, delay: 1.05 }}
                style={{ animation: "float-y 4.5s ease-in-out infinite", animationDelay: "1.5s" }}
                className="absolute -right-4 md:-right-14 top-[58%] -translate-y-1/2 card-paper pl-2.5 pr-3.5 py-2 z-20"
            >
                <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-[var(--cream-2)] flex items-center justify-center text-[var(--ink)]">
                        <i className={`fa-solid ${c.metricB.icon} text-[11px]`} />
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`mB-${clientIdx}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35 }}
                        >
                            <div className="font-display font-bold text-[17px] text-[var(--ink)] leading-none tabular-nums">{c.metricB.value}</div>
                            <div className="font-mono text-[9px] tracking-[0.14em] text-[var(--ink-dim)] uppercase mt-1 leading-tight">{c.metricB.label}</div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default HeroSection;
